import React, { useState, useEffect } from "react";
import { useContent } from "../context/ContentContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { toast } from "sonner";
import {
  Lock,
  Pencil,
  Save,
  LogOut,
  Eye,
  RotateCcw,
  Palette,
  Undo2,
  Redo2,
} from "lucide-react";

export default function EditToolbar() {
  const {
    isAdmin,
    editMode,
    setEditMode,
    dirty,
    saving,
    login,
    logout,
    save,
    resetContent,
    content,
    updateTheme,
    canUndo,
    canRedo,
    undo,
    redo,
    sessionExpired,
  } = useContent();

  const [loginOpen, setLoginOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // Session expired mid-edit -> prompt to log back in (unsaved edits are kept)
  useEffect(() => {
    if (sessionExpired) setLoginOpen(true);
  }, [sessionExpired]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const resume = sessionExpired && dirty;
      const newToken = await login(password);
      setLoginOpen(false);
      setEditMode(true);
      setPassword("");
      if (resume) {
        try {
          await save(newToken);
          toast.success("Logged back in \u2013 your changes were saved");
        } catch {
          toast.error("Logged in, but saving failed. Please click Save.");
        }
      } else {
        toast.success("Logged in \u2013 edit mode enabled");
      }
    } catch (err) {
      toast.error("Incorrect password");
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    try {
      await save();
      toast.success("Changes saved");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        toast.error("Your session expired. Log in again to save \u2013 your edits are still here.");
      } else {
        toast.error(`Save failed${status ? ` (${status})` : ""}. Please try again.`);
      }
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset the whole page back to the original content?"))
      return;
    try {
      await resetContent();
      toast.success("Reset to original content");
    } catch (err) {
      toast.error("Reset failed");
    }
  };

  // Not logged in -> show lock button
  if (!isAdmin) {
    return (
      <>
        <button
          data-testid="admin-login-btn"
          aria-label="Admin login"
          onClick={() => setLoginOpen(true)}
          className="group fixed bottom-5 right-5 z-50 flex h-11 items-center rounded-full bg-[var(--navy)] px-[14px] text-sm font-semibold text-white shadow-xl transition-[padding] duration-200 hover:pr-4"
        >
          <Lock size={16} className="shrink-0" />
          <span className="max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-[max-width,opacity,margin] duration-200 group-hover:ml-2 group-hover:max-w-[60px] group-hover:opacity-100">
            Admin
          </span>
        </button>
        <LoginDialog
          open={loginOpen}
          setOpen={setLoginOpen}
          password={password}
          setPassword={setPassword}
          onSubmit={handleLogin}
          busy={busy}
          expired={sessionExpired}
          hasUnsaved={dirty}
        />
      </>
    );
  }

  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-2">
      {editMode && (
        <div className="flex flex-wrap items-center justify-end gap-2">
          {/* Undo / Redo */}
          <div className="flex items-center overflow-hidden rounded-full bg-white shadow-lg">
            <button
              onClick={undo}
              disabled={!canUndo}
              title="Undo (Ctrl+Z)"
              className="flex items-center gap-1.5 px-3.5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:bg-white"
            >
              <Undo2 size={16} /> Undo
            </button>
            <span className="h-6 w-px bg-slate-200" />
            <button
              onClick={redo}
              disabled={!canRedo}
              title="Redo (Ctrl+Shift+Z)"
              className="flex items-center gap-1.5 px-3.5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:bg-white"
            >
              <Redo2 size={16} /> Redo
            </button>
          </div>

          {/* Global theme colors */}
          <Popover>
            <PopoverTrigger asChild>
              <button className="flex items-center gap-2 rounded-full bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-lg hover:bg-slate-50">
                <Palette size={16} /> Theme
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-60">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                Brand colors
              </p>
              <ThemeColor
                label="Navy"
                value={content.theme.navy}
                onChange={(v) => updateTheme({ navy: v })}
              />
              <ThemeColor
                label="Red"
                value={content.theme.red}
                onChange={(v) => updateTheme({ red: v })}
              />
              <ThemeColor
                label="Light blue"
                value={content.theme.lightblue}
                onChange={(v) => updateTheme({ lightblue: v })}
              />
            </PopoverContent>
          </Popover>

          <button
            data-testid="reset-btn"
            onClick={handleReset}
            className="flex items-center gap-2 rounded-full bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-lg hover:bg-slate-50"
          >
            <RotateCcw size={16} /> Reset
          </button>

          <button
            data-testid="save-btn"
            onClick={() => handleSave()}
            disabled={saving}
            className="flex items-center gap-2 rounded-full bg-[var(--red)] px-5 py-2.5 text-sm font-semibold text-white shadow-lg hover:opacity-90 disabled:opacity-60"
          >
            <Save size={16} />
            {saving ? "Saving..." : dirty ? "Save *" : "Saved"}
          </button>
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          data-testid="edit-toggle-btn"
          onClick={() => setEditMode((v) => !v)}
          className="flex items-center gap-2 rounded-full bg-[var(--navy)] px-5 py-3 text-sm font-semibold text-white shadow-xl transition-transform hover:scale-105"
        >
          {editMode ? (
            <>
              <Eye size={16} /> Preview
            </>
          ) : (
            <>
              <Pencil size={16} /> Edit
            </>
          )}
        </button>
        <button
          data-testid="logout-btn"
          onClick={() => {
            logout();
            toast("Logged out");
          }}
          title="Log out"
          className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-600 shadow-lg hover:bg-slate-50"
        >
          <LogOut size={16} />
        </button>
      </div>
    </div>
  );
}

function ThemeColor({ label, value, onChange }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-sm text-slate-700">{label}</span>
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 w-12 cursor-pointer rounded border border-slate-300 bg-white p-0.5"
      />
    </div>
  );
}

function LoginDialog({ open, setOpen, password, setPassword, onSubmit, busy, expired, hasUnsaved }) {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-sm" data-testid="login-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            {expired ? "Session expired" : "Admin login"}
          </DialogTitle>
          <DialogDescription data-testid="login-dialog-description">
            {expired
              ? hasUnsaved
                ? "Your admin session timed out. Log in again and your unsaved changes will be saved automatically."
                : "Your admin session timed out. Please log in again to continue editing."
              : "Enter the admin password to edit this page."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <Input
            type="password"
            autoFocus
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Button
            type="submit"
            disabled={busy || !password}
            className="w-full bg-[var(--navy)] hover:opacity-90"
          >
            {busy ? "Checking..." : "Log in"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
